import { areObjectsEqual } from "../../../../app/common";
import { isProdEnv } from "../../../../common/helpers/envHelpers";
import { AssignmentById } from "../../../talents/talentAPI";
import { WORKTIME_BANK_LEAVE_ABSENCE_TYPE } from "../../constants";
import {
  ContinuedLeave,
  Entry,
  LeaveEntity,
  LeaveEntry,
  UpdatableEntry,
} from "../../entriesAPI";
import { AbsenceListEntry } from "../Absences/AbsenceHoursList/AbsenceHoursList";

type EntryWithApprovalState = {
  approvalState: string;
};

type EntryWithApprovalResetData = EntryWithApprovalState &
  Pick<UpdatableEntry, "id" | "version">;

export const SICK_LEAVE_TYPES = [
  "MEDICAL_CHECK",
  "UNSUDDEN_MEDICAL_CHECK",
  "SICK_LEAVE",
  "SICK_LEAVE_COMMUTATIONAL_ACCIDENT",
  "SICK_LEAVE_OCCUPATIONAL_ACCIDENT",
  "SICK_LEAVE_OCCUPATIONAL_DISEASE",
  "SICK_LEAVE_SELF_REPORTED",
  "TEMPORARY_CARE_LEAVE",
  "UNSUDDEN_SICK_LEAVE",
  "SUDDEN_DENTAL_DISEASE",
];

export const CONTINUABLE_ABSENCE_TYPES = [
  "SICK_LEAVE",
  "SICK_LEAVE_COMMUTATIONAL_ACCIDENT",
  "SICK_LEAVE_OCCUPATIONAL_ACCIDENT",
  "SICK_LEAVE_OCCUPATIONAL_DISEASE",
  "SICK_LEAVE_SELF_REPORTED",
  "TEMPORARY_CARE_LEAVE",
  "UNSUDDEN_SICK_LEAVE",
  "SUDDEN_DENTAL_DISEASE",
];

export const MIXABLE_ABSENCE_TYPES = [
  "SICK_LEAVE",
  "SICK_LEAVE_SELF_REPORTED",
  "UNSUDDEN_SICK_LEAVE",
];

export const buildLeaveEntity = (talentId: string, entryData: LeaveEntry) => {
  const fetchedLeaveEntity: LeaveEntity = {
    ...entryData,
    talentId: talentId || null,
    dateRange: {
      startDate: entryData.startDate,
      endDate: entryData.endDate,
    },
    continuationOf: entryData.continuationOf ? entryData.continuationOf : null,
    continuationOfId: entryData.continuationOf
      ? entryData.continuationOf.id
      : null,
    entries: entryData.entries,
    previousLeaves: { order: [], chains: {} },
  };
  return fetchedLeaveEntity;
};

// Step 4: Define the function to find and filter entries based on leaveId
export const findReduxEntries = (
  leaveEntityId: string | null,
  absenceEntries: Entry[],
): AbsenceListEntry[] => {
  // Filter entries so that only ones sharing the same leaveId or are unassigned are shown on the list
  const filteredEntries = absenceEntries.filter((entry) => {
    return entry.leaveId === leaveEntityId || !entry.leaveId;
  });

  // Map filtered entries to modify checked value based on leaveEntity entries
  const foundEntries = filteredEntries.map((entry) => {
    return {
      ...entry,
      checked: leaveEntityId && entry.leaveId === leaveEntityId, // Checked if part of current leave
    } as AbsenceListEntry;
  });

  return foundEntries;
};

const checkSharedSaveConditions = (
  checkedEntries: AbsenceListEntry[],
  leaveEntity: LeaveEntity,
  inputErrors: { [code: string]: boolean },
) => {
  return (
    // If leaveEntity is new and no entries are checked
    (leaveEntity.version === 0 && checkedEntries.length === 0) ||
    // If any of checked entries is missing assignmentId or customerCostCenterId
    checkedEntries.some(
      (entry) => !entry.assignmentId || !entry.customerCostCenterId,
    ) ||
    Object.values(inputErrors).includes(true)
  );
};

// TODO: This list is different from the backend's requiresLeaveAttachment list
export const ABSENCE_TYPES_REQUIRING_ATTACHMENT = [
  "SICK_LEAVE",
  "SICK_LEAVE_COMMUTATIONAL_ACCIDENT",
  "SICK_LEAVE_OCCUPATIONAL_ACCIDENT",
  "SICK_LEAVE_OCCUPATIONAL_DISEASE",
  "UNSUDDEN_SICK_LEAVE",
  "TEMPORARY_CARE_LEAVE",
  "QUARANTINE",
  "MEDICAL_CHECK",
  "UNSUDDEN_MEDICAL_CHECK",
  "SUDDEN_DENTAL_DISEASE",
];

const APPROVAL_STATES_REQUIRING_RESET = [
  "Approved",
  "Rejected",
  "CustomerRejected",
];

export const hasEntriesNeedingApprovalReset = (
  entries: EntryWithApprovalState[],
): boolean =>
  entries.some((entry) =>
    APPROVAL_STATES_REQUIRING_RESET.includes(entry.approvalState),
  );

export const hasApprovedEntries = (
  entries: EntryWithApprovalState[],
): boolean => entries.some((entry) => entry.approvalState === "Approved");

export const toApprovalResetPayload = (
  entries: EntryWithApprovalResetData[],
): UpdatableEntry[] =>
  entries
    .filter((entry) =>
      APPROVAL_STATES_REQUIRING_RESET.includes(entry.approvalState),
    )
    .map((entry) => ({ id: entry.id, version: entry.version }));

export const disableApproveButton = (
  entries: AbsenceListEntry[],
  inputErrors: { [code: string]: boolean },
  leaveEntity: LeaveEntity,
  hasPendingAttachment = false,
  canBypassAttachmentCheck = false,
) => {
  const checkedEntries = entries.filter((entry) => entry.checked);

  // leaveEntity.leaveAttachment is the authoritative source (from leaveById).
  // Use leaveEntity as fallback.
  const leaveHasAttachment =
    (leaveEntity.leaveAttachment?.attachments?.length ?? 0) > 0;

  // Also check the selected leave type so that converting a WORK_ENTRY to a
  // sick leave type correctly blocks approval before an attachment is added.
  const isLeaveTypeRequiringAttachment =
    ABSENCE_TYPES_REQUIRING_ATTACHMENT.includes(leaveEntity.type);

  const anyCheckedEntryMissingRequiredAttachment =
    !canBypassAttachmentCheck &&
    !hasPendingAttachment &&
    !leaveHasAttachment &&
    (isLeaveTypeRequiringAttachment ||
      checkedEntries.some((entry) => entry.leaveAttachmentRequired)) &&
    checkedEntries.some((entry) => !entry.leaveAttachment?.attachments?.length);

  return (
    checkSharedSaveConditions(checkedEntries, leaveEntity, inputErrors) ||
    checkedEntries.length === 0 ||
    checkedEntries.every((entry) => entry.approvalState === "Approved") ||
    anyCheckedEntryMissingRequiredAttachment
  );
};

export const disableSaveButton = (
  leaveEntity: LeaveEntity,
  originalLeaveEntity: LeaveEntity | null,
  inputErrors: { [code: string]: boolean },
  entries: AbsenceListEntry[],
  originalEntries: AbsenceListEntry[],
  hasPendingAttachment = false,
) => {
  const checkedEntries = entries.filter((entry) => entry.checked);

  // ** Remove leaveAttachments and version from leaveEntities and entries before comparing them
  // Remove leaveAttachment from leaveEntity
  const {
    leaveAttachment,
    version,
    previousLeaves,
    ...leaveEntityWithoutAttachment
  } = leaveEntity;
  // Remove leaveAttachment from originalLeaveEntity
  const {
    leaveAttachment: originalLeaveAttachment,
    version: originalVersion,
    previousLeaves: originalPreviousLeaves,
    ...originalLeaveEntityWithoutAttachment
  } = originalLeaveEntity || {};
  // Remove leaveAttachment from entries
  const entriesWithoutAttachment = entries.map(
    ({ leaveAttachment, ...entry }) => entry,
  );
  // Remove leaveAttachment from originalEntries
  const originalEntriesWithoutAttachment = originalEntries.map(
    ({ leaveAttachment, ...entry }) => entry,
  );

  const entEqual = areObjectsEqual(
    leaveEntityWithoutAttachment,
    originalLeaveEntityWithoutAttachment,
  );

  const entriesEq = areObjectsEqual(
    entriesWithoutAttachment,
    originalEntriesWithoutAttachment,
  );
  if (hasPendingAttachment) return false;

  const ret =
    checkSharedSaveConditions(checkedEntries, leaveEntity, inputErrors) ||
    (leaveEntity.version !== 0 &&
      entEqual &&
      leaveEntity.version !== 0 &&
      entriesEq);
  return ret;
};

export const assignmentsAvailableForEntry = (
  assignments: AssignmentById[],
  entry: AbsenceListEntry,
): AssignmentById[] => {
  // Filter assignments based on the entry's start and end time
  return assignments.filter((assignment) => {
    const assignmentStart = assignment.details.start;
    const assignmentEnd = assignment.details.end ?? Infinity; // If end is missing, treat it as Infinity

    const entryStart = entry.start;
    const entryEnd = entry.end;

    // Entry's start and end time must fully fit inside the assignment's time range
    // OR entry's already set assignmentId must match the assignment's assignmentId
    return (
      (entryStart >= assignmentStart && entryEnd <= assignmentEnd) ||
      assignment.assignmentId === entry.assignmentId
    );
  });
};

// Utility function to check if a given date matches the start date of an entry
export const isDateWithinRange = (date: Date, entryStart: number): boolean => {
  const dateStart = new Date(date).setHours(0, 0, 0, 0); // Start of the day for the provided date
  const entryStartDate = new Date(entryStart).setHours(0, 0, 0, 0); // Start of the day for the entry

  // Return true if the provided date matches the entry's start date
  return dateStart === entryStartDate;
};

export const filterAbsenceTypes = (
  translatedEntryTypes: { [key: string]: string },
  availableAbsenceTypesInAssignment: string[] | undefined,
  priorityTypes: string[] = [], // Optional priority types, default to empty
  extraFilterList: string[] = [], // Optional extra filter (e.g., switchable types), default to empty
  isShift?: boolean,
) => {
  // List of entry types that are not shown in the list
  let hiddenEntryTypes = [
    "WORK_ENTRY", // Don't show work entry types as absence type
    "WAITING_TIME", // Don't show work entry types as absence type
    "STAND_BY_DUTY", // Don't show work entry types as absence type
    "PAID_TRAVEL_TIME", // Don't show work entry types as absence type

    "PATERNITY_LEAVE", // Retired absence type, TES texts changed
    "MATERNITY_LEAVE", // Retired absence type, TES texts changed
  ];

  if (isProdEnv()) {
    hiddenEntryTypes = [
      ...hiddenEntryTypes,
      "MIDWEEK_LEAVE", // Don't show this absence type in production yet (AP-vapaa)
    ];
  }

  // If isShift is true, add additional entry types to the hidden list
  if (!!isShift) {
    hiddenEntryTypes = [...hiddenEntryTypes, WORKTIME_BANK_LEAVE_ABSENCE_TYPE];
  }

  // Default absence types if none are provided in the assignment
  // Basically whole list of entry types (WorktimeEntryTypes.kt), work entry types excluded
  const defaultAbsenceTypes = Object.keys(translatedEntryTypes).filter(
    (key) => !hiddenEntryTypes.includes(key), // Filter out hidden entry types
  );

  // Available absence types in the assignment
  const availableAbsenceTypes: string[] = (
    availableAbsenceTypesInAssignment || []
  ).filter(
    (type) => !hiddenEntryTypes.includes(type), // Filter out hidden entry types
  );

  // Use availableAbsenceTypes if they exist, otherwise, fallback to defaultAbsenceTypes
  const desiredAbsenceTypes =
    availableAbsenceTypes.length > 0
      ? availableAbsenceTypes
      : defaultAbsenceTypes;

  // If priorityTypes are provided, filter and sort them; otherwise, skip this part
  const prioritizedAbsenceTypes =
    priorityTypes.length > 0
      ? desiredAbsenceTypes
          .filter((type) => priorityTypes.includes(type))
          .sort((a, b) => priorityTypes.indexOf(a) - priorityTypes.indexOf(b)) // Take order from priorityTypes
      : [];

  // Sort remaining types alphabetically based on translatedEntryTypes
  const restAbsenceTypes = desiredAbsenceTypes
    .filter((type) => !priorityTypes.includes(type))
    .sort((a, b) => {
      const nameA = translatedEntryTypes[a] || a;
      const nameB = translatedEntryTypes[b] || b;
      return nameA.localeCompare(nameB, "fi");
    });

  // Combine prioritized types with the sorted rest
  let combinedAbsenceTypes = [...prioritizedAbsenceTypes, ...restAbsenceTypes];

  // Apply extra filtering (e.g., switchable types) if provided
  if (extraFilterList.length > 0) {
    combinedAbsenceTypes = combinedAbsenceTypes.filter((type) =>
      extraFilterList.includes(type),
    );
  }

  return combinedAbsenceTypes;
};

export const isAllowedContinuationOfType = (type: string) => {
  // All sick leave types and temporary care leave are allowed to continue themselves.
  return CONTINUABLE_ABSENCE_TYPES.includes(type);
};

export const isAllowedContinuationOfTypeFor = (
  ourType: string,
  newType: string,
) => {
  // Only some absence types can continue each other.
  if (
    MIXABLE_ABSENCE_TYPES.includes(ourType) &&
    MIXABLE_ABSENCE_TYPES.includes(newType)
  ) {
    return true;
  }
  return ourType === newType;
};

/**
 * Formats the date string for continued leaves. Currently, it seems to use funny way to convert
 * between YYYY-MM-DD and DD.MM.YYYY, and gives out comma separated list of dates.
 * @param leaves array of ContinuedLeave objects belonging to the same leave (in correct order)
 * @returns Comma separated string of dates in the format DD.MM.YYYY or DD.MM.YYYY - DD.MM.YYYY
 * @example
 * continuedLeaveDateString([
 *   { startDate: "2023-10-05", endDate: "2023-10-05" },
 *   { startDate: "2023-10-02", endDate: "2023-10-03" },
 *   { startDate: "2023-10-01", endDate: "2023-10-01" },
 * ]);
 * // returns "05.10.2023, 01.10.2023 - 03.10.2023"
 */
export function continuedLeaveDateString(leaves: ContinuedLeave[]): string {
  if (!leaves || leaves.length === 0) return "";

  // Sort leaves by startDate in descending order
  const sortedLeaves = leaves.sort(
    (a, b) => new Date(b.startDate).getTime() - new Date(a.startDate).getTime(),
  );

  const formattedRanges: string[] = [];
  let currentRangeStart = sortedLeaves[0].startDate;
  let currentRangeEnd = sortedLeaves[0].endDate;

  for (let i = 1; i < sortedLeaves.length; i++) {
    const currentLeave = sortedLeaves[i];
    const previousLeaveStart = new Date(currentLeave.endDate);
    const currentLeaveEnd = new Date(currentRangeStart);

    // Check if the current leave is consecutive
    if (
      currentLeaveEnd.getTime() - previousLeaveStart.getTime() <=
      24 * 60 * 60 * 1000
    ) {
      currentRangeStart = currentLeave.startDate;
    } else {
      // Push the current range and start a new one
      formattedRanges.push(formatRange(currentRangeStart, currentRangeEnd));
      currentRangeStart = currentLeave.startDate;
      currentRangeEnd = currentLeave.endDate;
    }
  }

  // Push the last range
  formattedRanges.push(formatRange(currentRangeStart, currentRangeEnd));

  return formattedRanges.join(", ");
}

function formatRange(startDate: string, endDate: string): string {
  if (startDate === endDate) {
    return formatDate(startDate);
  }
  return `${formatDate(startDate)} - ${formatDate(endDate)}`;
}

function formatDate(date: string): string {
  const [year, month, day] = date.split("-");
  return `${day}.${month}.${year}`;
}

export const isLeaveAttachmentRequiredButMissing = (entry: Entry): boolean =>
  ABSENCE_TYPES_REQUIRING_ATTACHMENT.includes(entry.type) &&
  !entry.leaveAttachment?.attachments?.length;
